import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // MYK speed (agent E, 2026-09-25): the phone carousel keeps every slide after the first lazy (the
    // first one is the LCP image and must not share bandwidth). A lazy slide inside the horizontal
    // scroller only starts loading once it is actually on screen, so a swipe showed an empty slide
    // for a moment on 4G (the film poster of media #2 is 60-140 KB). Now the neighbours of the
    // current slide are switched to eager as soon as the shopper touches the gallery or a slide
    // changes: "about to be shown", never during the initial page load.
    this.addEventListener('slideshow:select', this.#handleSlideSelect, { signal });
    for (const type of ['pointerdown', 'touchstart', 'focusin']) {
      this.addEventListener(type, this.#handleIntent, { signal, passive: true });
    }
  }

  /**
   * Starts loading the images of the slides next to `index` (MYK).
   * @param {number} index
   */
  #warmNeighbours(index) {
    const slides = this.querySelectorAll('slideshow-component slideshow-slide');
    for (const i of [index + 1, index - 1]) {
      const slide = slides[i];
      if (!slide) continue;
      for (const img of slide.querySelectorAll('img.product-media__image[loading="lazy"]')) {
        img.loading = 'eager';
      }
    }
  }

  /** @param {Event} event */
  #handleSlideSelect = (event) => {
    const detail = /** @type {any} */ (event).detail;
    // The slideshow also selects slide 0 by itself while it initialises: ignore that one.
    if (!detail?.userInitiated || typeof detail.index !== 'number') return;
    this.#warmNeighbours(detail.index);
  };

  #handleIntent = () => {
    // Desktop shows the grid and hides the carousel (display: none): nothing to warm there.
    const slideshow = this.slideshow;
    if (!slideshow || slideshow.offsetParent === null) return;
    this.#warmNeighbours(slideshow.current ?? 0);
  };

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
